/**
 * Leaderboards (website roadmap S25 — FF Scouter's leaderboards; TornStats'/Torn Intel's top lists). One copy for the
 * server (apps/api/src/leaderboards.ts builds them once a day), the website (pages/leaderboards.ts) and the tests.
 *
 * What is ranked, and from where — nothing a player can't already see in Omni-Torn, except the ranking itself:
 * - Battle stats: Omni-Torn's best estimate of each player (the player database's `players.total` — the same number the
 *   player page, Spies & estimates and the Factions page show), rounded to 2 significant figures; estimates worked out from
 *   rank alone are left out (a wide guess). Never anyone's battle stats themselves.
 * - Public personal stats: Torn's Hall of Fame (the top 100 of a board, Torn's own public list, read with lent keys or the
 *   server's own key once a day) and the player database's `personalstats` sightings — ONLY rows whose sender had agreed to
 *   LEADERBOARD_TERMS when they sent them (each row keeps its sender's version), never a sighting about the sender
 *   themself (the player database never takes those).
 * - Factions: Torn's faction Hall of Fame (respect, best chain) and the sum of Omni-Torn's estimates of each faction's
 *   members (the Factions page shows each member's).
 * - Activity: the hours a player was active in the last 7 days as the activity store saw them (S22, shown to Omni-Torn
 *   users from terms 10 on the player and faction pages).
 * - Quarterly and event boards: what each public-stat board's players gained over a quarter or over one of Torn's events
 *   (Torn's calendar), worked out from the boards' daily snapshots (kept LEADERBOARD_KEEP_DAYS).
 * Invite-only (`user()`), like every page that shows players.
 */

/** From this terms version on, a sender's public-stat sightings may be ranked on the leaderboards (the terms table says so). */
export const LEADERBOARD_TERMS = 13;
/** Rows shown of a board (the page's "top 250"). */
export const LEADERBOARD_SIZE = 250;
/** Rows kept of each board a day (so a faction filter still finds its members below the top 250). */
export const LEADERBOARD_KEEP = 1_000;
/** Daily snapshots of the public-stat boards are kept this long (the last quarter and this one, and the events in them). */
export const LEADERBOARD_KEEP_DAYS = 200;
/** The activity board's window (days). */
export const LEADERBOARD_ACTIVITY_DAYS = 7;

export type LeaderboardKind = 'estimate' | 'stat' | 'faction' | 'activity';

export interface LeaderboardDef {
  id: string;
  kind: LeaderboardKind;
  label: string;
  /** Torn's Hall of Fame board (v2 /torn/hof?cat=…) — player boards. */
  hof?: string;
  /** Torn's faction Hall of Fame board (v2 /torn/factionhof?cat=…). */
  factionHof?: string;
  /** The field in the player database's `personalstats` sightings (Torn's personal stat name). */
  ps?: string;
  money?: boolean;
  /** Seconds. */
  time?: boolean;
}

export const LEADERBOARDS: readonly LeaderboardDef[] = [
  { id: 'estimate', kind: 'estimate', label: 'Battle stats (Omni-Torn’s estimate)' },
  { id: 'networth', kind: 'stat', label: 'Networth', hof: 'networth', ps: 'networth', money: true },
  { id: 'level', kind: 'stat', label: 'Level', hof: 'level' },
  { id: 'attackswon', kind: 'stat', label: 'Attacks won', hof: 'attacks', ps: 'attackswon' },
  { id: 'defendswon', kind: 'stat', label: 'Defends won', hof: 'defends', ps: 'defendswon' },
  { id: 'criminaloffenses', kind: 'stat', label: 'Criminal offenses', hof: 'offenses', ps: 'criminaloffenses' },
  { id: 'busts', kind: 'stat', label: 'Busts', hof: 'busts', ps: 'peoplebusted' },
  { id: 'revives', kind: 'stat', label: 'Revives', hof: 'revives', ps: 'revives' },
  { id: 'awards', kind: 'stat', label: 'Awards', hof: 'awards' },
  { id: 'xantaken', kind: 'stat', label: 'Xanax taken', ps: 'xantaken' },
  { id: 'refills', kind: 'stat', label: 'Energy refills', ps: 'refills' },
  { id: 'energydrinkused', kind: 'stat', label: 'Energy cans used', ps: 'energydrinkused' },
  { id: 'boostersused', kind: 'stat', label: 'Boosters used', ps: 'boostersused' },
  { id: 'statenhancersused', kind: 'stat', label: 'Stat enhancers used', ps: 'statenhancersused' },
  { id: 'useractivity', kind: 'stat', label: 'Time played', ps: 'useractivity', time: true },
  { id: 'attacksassisted', kind: 'stat', label: 'Attacks assisted', ps: 'attacksassisted' },
  { id: 'rankedwarhits', kind: 'stat', label: 'Ranked war hits', ps: 'rankedwarhits' },
  { id: 'alcoholused', kind: 'stat', label: 'Alcohol used', ps: 'alcoholused' },
  { id: 'activity', kind: 'activity', label: 'Most active (last 7 days)' },
  { id: 'f-respect', kind: 'faction', label: 'Factions: respect', factionHof: 'respect' },
  { id: 'f-chain', kind: 'faction', label: 'Factions: best chain', factionHof: 'chain' },
  { id: 'f-estimate', kind: 'faction', label: 'Factions: estimated battle stats (members added up)' },
];

export const leaderboardDef = (id: string): LeaderboardDef | null => LEADERBOARDS.find((b) => b.id === id) ?? null;

/** Torn calls one daily round makes: each Hall of Fame board once, each faction board once, and Torn's calendar. */
export const LEADERBOARD_DAILY_CALLS = LEADERBOARDS.filter((b) => b.hof || b.factionHof).length + 1;

/** Where a row's value came from: Torn's Hall of Fame, an Omni-Torn user's sighting, the player database's estimate, the activity store. */
export type LeaderboardSource = 'hof' | 'seen' | 'estimate' | 'activity';

export interface LeaderboardRow {
  /** Place on this board (1 = top), among the players/factions Omni-Torn knows of. */
  rank: number;
  /** A player's id (a faction's on faction boards). */
  id: number;
  name: string | null;
  level: number | null;
  /** The player's faction (player boards). */
  factionId: number | null;
  value: number;
  src: LeaderboardSource;
  /** When the value was true (ms): Torn's list read, the sighting, the estimate, the build. */
  at: number;
  /** Torn's own Hall of Fame place (a value from Torn's list). */
  place?: number | null;
  /** Estimates: how the heaviest one was worked out ('fight', 'pool', 'spy'). */
  method?: string | null;
  /** Faction boards: members (Torn's count, or how many Omni-Torn has an estimate of). */
  members?: number | null;
  /** Faction estimate board: members with an estimate. Activity: hours Omni-Torn saw them (on or off). */
  covered?: number | null;
  /** A period's gains: the value at its start (`value` is the gain). */
  start?: number | null;
}

export interface LeaderboardPeriod {
  /** 'q2026-4', or 'e<start day>-<words>' for an event. */
  id: string;
  kind: 'quarter' | 'event';
  label: string;
  /** UTC day numbers (ms / 86 400 000), both included. */
  fromDay: number;
  toDay: number;
}

/** GET /api/leaderboards — the boards, when they were built, and the periods that can be asked. */
export interface LeaderboardIndex {
  boards: Array<Pick<LeaderboardDef, 'id' | 'kind' | 'label'>>;
  /** When the boards were last built (ms; null: not yet). */
  builtAt: number | null;
  /** When Torn's lists were last read (ms; null: not yet). */
  tornAt: number | null;
  periods: LeaderboardPeriod[];
  /** Days of snapshots kept so far. */
  days: number;
  termsNeeded: number;
}

/** GET /api/leaderboards/:board[?period=&faction=]. */
export interface LeaderboardAnswer {
  board: string;
  builtAt: number | null;
  /** The period asked (gains), or null for the board as it is. */
  period: LeaderboardPeriod | null;
  /** A period's gains: the snapshot days actually compared (UTC day numbers). */
  fromDay?: number | null;
  toDay?: number | null;
  factionId: number | null;
  rows: LeaderboardRow[];
  /** Rows the board has in all (before LEADERBOARD_SIZE). */
  total: number;
  note?: string;
}

// ---------------------------------------------------------------- periods

const DAY = 86_400_000;
export const lbDayOf = (ms: number): number => Math.floor(ms / DAY);

/** Torn's quarters are the calendar's (UTC): the one `ms` is in. */
export function quarterOf(ms: number): { year: number; q: number; fromDay: number; toDay: number } {
  const d = new Date(ms);
  const year = d.getUTCFullYear();
  const q = Math.floor(d.getUTCMonth() / 3) + 1;
  const from = Date.UTC(year, (q - 1) * 3, 1);
  const to = Date.UTC(year, q * 3, 1) - 1;
  return { year, q, fromDay: lbDayOf(from), toDay: lbDayOf(to) };
}

const slug = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'event';

/**
 * The periods worth offering at `now`: this quarter (so far) and the last one, and Torn's events (from its calendar,
 * ms entries) that started on or after the oldest snapshot kept could cover and have begun — newest first.
 */
export function leaderboardPeriods(now: number, calendar: ReadonlyArray<{ title: string; start: number; end: number }>): LeaderboardPeriod[] {
  const today = lbDayOf(now);
  const cur = quarterOf(now);
  const prev = quarterOf(Date.UTC(cur.year, (cur.q - 1) * 3, 1) - 1);
  const out: LeaderboardPeriod[] = [
    { id: `q${cur.year}-${cur.q}`, kind: 'quarter', label: `Q${cur.q} ${cur.year} (so far)`, fromDay: cur.fromDay, toDay: Math.min(cur.toDay, today) },
    { id: `q${prev.year}-${prev.q}`, kind: 'quarter', label: `Q${prev.q} ${prev.year}`, fromDay: prev.fromDay, toDay: prev.toDay },
  ];
  const oldest = today - LEADERBOARD_KEEP_DAYS;
  const events = calendar
    .filter((e) => e.title && Number.isFinite(e.start) && Number.isFinite(e.end) && e.end > e.start && e.start <= now && lbDayOf(e.start) >= oldest)
    .sort((a, b) => b.start - a.start);
  const seen = new Set<string>();
  for (const e of events) {
    const fromDay = lbDayOf(e.start);
    const id = `e${fromDay}-${slug(e.title)}`;
    if (seen.has(id)) continue;
    seen.add(id);
    const on = e.end > now;
    out.push({ id, kind: 'event', label: `${e.title.slice(0, 60)} ${new Date(e.start).getUTCFullYear()}${on ? ' (so far)' : ''}`, fromDay, toDay: Math.min(lbDayOf(e.end - 1), today) });
  }
  return out;
}

/**
 * Gains over a period from two snapshots (player id → value): only players in both, only what went up, biggest first.
 * Pure: the server picks the snapshot days (the newest on or before the period's first day — else its first kept day —
 * and the newest on or before its last).
 */
export function periodGains(start: ReadonlyMap<number, number>, end: ReadonlyMap<number, number>): Array<{ id: number; gain: number; from: number; to: number }> {
  const out: Array<{ id: number; gain: number; from: number; to: number }> = [];
  for (const [id, to] of end) {
    const from = start.get(id);
    if (from === undefined || !(to > from)) continue;
    out.push({ id, gain: to - from, from, to });
  }
  return out.sort((a, b) => b.gain - a.gain || a.id - b.id);
}

/**
 * One public-stat board from Torn's Hall of Fame rows and Omni-Torn users' sightings: per player the newer of the two
 * (Torn's list wins a tie), biggest first, ranked 1…, at most `keep`.
 */
export function mergeStatBoard(
  hof: ReadonlyArray<{ id: number; name: string | null; level: number | null; factionId: number | null; value: number; place: number | null; at: number }>,
  seen: ReadonlyArray<{ id: number; value: number; at: number }>,
  facts: (id: number) => { name: string | null; level: number | null; factionId: number | null } | null,
  keep: number = LEADERBOARD_KEEP,
): LeaderboardRow[] {
  const by = new Map<number, Omit<LeaderboardRow, 'rank'>>();
  for (const r of hof) {
    if (!Number.isFinite(r.value)) continue;
    by.set(r.id, { id: r.id, name: r.name, level: r.level, factionId: r.factionId, value: r.value, src: 'hof', at: r.at, place: r.place });
  }
  for (const s of seen) {
    if (!Number.isFinite(s.value) || s.value < 0) continue;
    const have = by.get(s.id);
    if (have && have.at >= s.at) continue;
    const f = facts(s.id);
    by.set(s.id, { id: s.id, name: f?.name ?? have?.name ?? null, level: f?.level ?? have?.level ?? null, factionId: f?.factionId ?? have?.factionId ?? null, value: s.value, src: 'seen', at: s.at });
  }
  return rankRows([...by.values()], keep);
}

/** Biggest first (ties: the lower id), ranked 1…, at most `keep`. */
export function rankRows(rows: ReadonlyArray<Omit<LeaderboardRow, 'rank'>>, keep: number = LEADERBOARD_KEEP): LeaderboardRow[] {
  return [...rows]
    .sort((a, b) => b.value - a.value || a.id - b.id)
    .slice(0, keep)
    .map((r, i) => ({ ...r, rank: i + 1 }));
}

/** A personal stat's value in a sighting (a number, or Torn's `{ value }`), or null. */
export function sightingValue(data: Record<string, unknown>, field: string): number | null {
  const v = data[field];
  const n = typeof v === 'number' ? v : v && typeof v === 'object' && typeof (v as { value?: unknown }).value === 'number' ? (v as { value: number }).value : typeof v === 'string' && /^\d+(\.\d+)?$/.test(v) ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 && n < 1e16 ? n : null;
}

/** Set bits in a bitmap (activity slots). */
export function countBits(b: Uint8Array): number {
  let n = 0;
  for (let i = 0; i < b.length; i++) {
    let x = b[i]!;
    while (x) {
      n += x & 1;
      x >>= 1;
    }
  }
  return n;
}
